import { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { searchCityGouv, type AdresseGouvSuggestion } from '../utils/geo';

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Sélection d'une ville : remplit CP/ville/département */
  onPick: (s: AdresseGouvSuggestion) => void;
  placeholder?: string;
  className?: string;
}

const defaultCls =
  'w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50';

export default function CityAutocomplete({ value, onChange, onPick, placeholder = 'Ex : Reims', className }: Props) {
  const [suggestions, setSuggestions] = useState<AdresseGouvSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = (q: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchCityGouv(q);
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleChange = (v: string) => {
    onChange(v);
    fetchSuggestions(v);
  };

  const handlePick = (s: AdresseGouvSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    onPick(s);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={className || defaultCls}
        autoComplete="off"
      />
      {loading && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 animate-spin" strokeWidth={1.5} />}
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 shadow-xl">
          {suggestions.map((s, idx) => (
            <button
              key={`${s.label}-${idx}`}
              type="button"
              onClick={() => handlePick(s)}
              className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-amber-500/10 hover:text-amber-300 border-b border-slate-800 last:border-b-0 flex items-start gap-2"
            >
              <MapPin size={12} className="text-amber-400 mt-0.5 shrink-0" strokeWidth={1.5} />
              <div className="min-w-0">
                <div className="truncate">{s.label}</div>
                {s.context && <div className="text-[10px] text-slate-500 truncate">{s.context}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
